<template>
  <div
    ref="datepickerRef"
    class="hasDatepicker"
    :class="[{ 'inline-mode': inline, 'has-btn': reset,  'positon-right' : !resetPostion}, dateClass]"
  >
    <!--
      20250519 
      1. 추가 년도/월 선택버튼 수정 :enable-month-year-select="true"
      2. 삭제 년도/월 커스텀 :month-year-component="monthYearComponent"
      3. 삭제 Datepicker 안에 템플릿 삭제 
      20250522 전체 레이아웃 및 스타일 수정
    -->
    <Datepicker v-model="selectedDate" v-bind="getOptions" >
      <template #day="{ day, date }">
        <template v-if="calcDate(date) === today">
          {{ day }}
          <span>오늘</span>
        </template>
        <template v-else>
          {{ day }}
        </template>
      </template>
    </Datepicker>

    <!-- 초기화 (s) -->
    <button
      type="button"
      class="btn-type05 v2 btn-reset"
      @click="selectedDateClear"
      v-if="reset"
    >
      초기화
    </button>
    <!-- //초기화 (e) -->
  </div>
</template>
<script setup lang="ts">
import { ref, watch, computed, defineExpose } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import type { VueDatePickerProps } from "@vuepic/vue-datepicker";

defineOptions({
  name: "DatepickerComponent",
});

// props
interface Props {
  disabledDates?: Date[] | ((date: Date) => boolean);
  disablePastDates?: boolean;
  disableInRange?: boolean; //비활성 날짜 포함 여부
  disabled?: boolean; //비활성
  range?: boolean;
  inline?: boolean;
  multiCalendar?: boolean;
  autoApply?: boolean;
  placeholder?: string;
  dateInput?: any;
  reset?: boolean; //초기화 버튼 여부
  custom?: string; //커스텀
  resetPostion?:boolean; 
  defaultInput?: any; // 인풋에 기본날짜 지정
  modelValue?: Date[] | [null, null] | null
  dateClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  disabledDates: () => [],
  disablePastDates: false,
  disableInRange: true,
  range: false,
  inline: false,
  multiCalendar: false,
  autoApply: true,
  placeholder: "날짜를 선택하세요",
  reset: false,
  custom: "",
  defaultInput: undefined,
  dateClass: ''
});

const isDateDisabled = computed(() => {
  return (date: Date) => {
    // 오늘 날짜 포함하여 이전 날짜 체크 (disablePastDates가 true일 때만)
    if (props.disablePastDates) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date <= today) return true; // '<' 에서 '<=' 로 변경
    }

    // props로 전달된 disabledDates 체크
    if (typeof props.disabledDates === "function") {
      return props.disabledDates(date);
    } else if (Array.isArray(props.disabledDates)) {
      return props.disabledDates.some((disabledDate) => {
        const disabled = new Date(disabledDate);
        return (
          disabled.getFullYear() === date.getFullYear() &&
          disabled.getMonth() === date.getMonth() &&
          disabled.getDate() === date.getDate()
        );
      });
    }
    return false;
  };
});

const emit = defineEmits(["updateDate"]); //업데이트 날짜
type ModelValue = VueDatePickerProps["modelValue"];
const translations = {
  days: ["일", "월", "화", "수", "목", "금", "토"],
  months: [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ],
} as const;


const getOptions = computed<any>(() => {
  return {
    inline: props.inline,
    autoApply: props.autoApply,
    selectText: "선택완료",
    placeholder: props.placeholder,
    disabled: props.disabled,
    translations: translations,
    disabledDates: isDateDisabled.value,
    clearable: false,
    weekStart: 0,
    offset: 8,
    mode: "date",
    format: "yyyy-MM-dd",
    locale: "ko",
    enableTimePicker: false,
    calendarClass: "custom-calendar-icon",
    enableMonthYearSelect: true,
    multiCalendars: props.range ? 2 : props.multiCalendar,
    range: props.range,
    multiCalendarsSolo: true,
    textInput: {
      rangeSeparator: " ~ ",
    },
  }
});

const datepickerRef = ref<HTMLElement | null>(null);
const selectedDate = ref<ModelValue>(null);

// 20250930 인풋에 기본 값 출력
onMounted(() => {
  if (props.defaultInput && props.range) {
    const today = new Date();
    const start = new Date(today);
    const end = new Date(today);

    start.setDate(today.getDate() + props.defaultInput[0]);
    end.setDate(today.getDate() + props.defaultInput[1]);

    selectedDate.value = [start, end];
  }
});

const selectedDateClear = () => {
  selectedDate.value = "";
};

const today = ref(
  `${new Date().getFullYear()}${new Date().getMonth()}${new Date().getDate()}`
);

const calcDate = (date: Date) => {
  return `${date.getFullYear()}${date.getMonth()}${date.getDate()}`;
};

watch(() => props.modelValue, (newVal) => {
  selectedDate.value = newVal as ModelValue 
},
{ immediate: true, deep: true }
)

watch(selectedDate, (newValue) => {
  const element = datepickerRef.value;
  if (element) {
    if (newValue) {
      element.classList.add("focus");
    } else {
      element.classList.remove("focus");
    }
  }

  if (props.range === true) {
    let periodDatePicker: Date[] = [];
    if (selectedDate.value) {
      periodDatePicker = Object.values(selectedDate.value);
    }
    const [start, end] = periodDatePicker;

    // range 인 경우 중간에 비활성화 날짜 체크
    if (
      start &&
      end &&
      Array.isArray(props.disabledDates) &&
      !props.disableInRange
    ) {
      const isBlocked = props.disabledDates.some((d) => d > start && d < end);
      if (isBlocked) {
        alert("비활성화 포함");
        selectedDate.value = null;
        return;
      }
    }

    const days = ["일", "월", "화", "수", "목", "금", "토"];

    if (props.custom === "bus") {
      //고속버스 인 경우
      const formatDate = (d: Date) => {
        const day = days[d.getDay()];
        return `${d.getFullYear()}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.${d
          .getDate()
          .toString()
          .padStart(2, "0")}(${day})`;
      };
      const formatted1 = `${formatDate(start)}`;
      const formatted2 = `${formatDate(end)}`;

      emit("updateDate", {
        start: formatted1,
        end: formatted2,
      });
    } else {
      const formatDate = (d: Date) =>
        `${d.getFullYear()}.${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}.${d.getDate().toString().padStart(2, "0")}`;
      const formatted = `${formatDate(start)} ~ ${formatDate(end)}`;
      emit("updateDate", formatted);
    }
  } else {
    if (props.custom === "bus") {
      //고속버스 인 경우
      const formatDate = (d: Date) => {
        const days = ["일", "월", "화", "수", "목", "금", "토"];
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const date = d.getDate().toString().padStart(2, "0");
        const day = days[d.getDay()];

        return `${year}.${month}.${date}(${day})`;
      };
      const formatted = formatDate(selectedDate.value as Date);
      emit("updateDate", {
        start: formatted,
      });
    } else {
      const formatDate = (d: Date) => {
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const date = d.getDate().toString().padStart(2, "0");

        return `${year}.${month}.${date}`;
      };
      const formatted = formatDate(selectedDate.value as Date);
      emit("updateDate", formatted);
    }
  }
});

defineExpose({ selectedDateClear });
</script>

<style lang="scss" scoped>
@use "@/assets/scss/pc/common/variables-hcon" as v;

.hasDatepicker{ 
  width: 100%;
  height: 40px;

	&.md {
		height: 48px;
	}

	&.lg {
		height: 56px;
	}

  :deep(.dp__main) {
    width: 100%;
		height: 100%;

    > div {
			height: 100%;
		}
  }
	:deep(.custom-month-year) {
    font-size: 14px;
    font-weight: 500;
    color: #1e1e1e;
    pointer-events: none;
  }
  :deep(.dp__input) { 
    width: 100%;
		height: 100%;
		padding: 0 42px 0 12px;
		border: 1px solid #999999;
		border-radius: 8px;
		font-size: 14px;
		font-weight: 400;
		line-height: 40px;
		color: v.$font-main-color;

		&:read-only,
		&:disabled {
			background-color: #dfdfdf;
			border: 1px solid #cccccc;
			color: v.$font-tertiary-color;

			&:hover,
			&:focus {
				border-color: #0a583f;
			}
		}
		&::-webkit-input-placeholder {
			color: v.$font-tertiary-color;
		}
		&::placeholder {
			color: v.$font-tertiary-color;
		}
		&:hover,
		&:focus {
			border-color: #0a583f;
		}
	}
	.dp__input_wrap {
		height: 100%;
	}
	:deep(.dp__input_wrap) {
		height: 100%;
		&:after {
			content: '';
			display: block;
			position: absolute;
			right: 16px;
			transform: translateY(-50%);
			top: 50%;
			background-image: url('@/assets/images/pc/icon-hcon/icon-calendar.svg');
			background-position: center;
			background-repeat: no-repeat;
			background-size: contain;
			width: 20px;
			height: 20px;
		}
	}

	:deep(.dp__input_icon) {
		display: none;
	}
  :deep(.dp--menu-wrapper) {
		left: 0 !important;
		transform: none !important;
		right: auto;
		.dp__menu {
    	width: 100%;
			border-radius: 8px;
			.dp__arrow_top {
				display: none;
			}
			.dp--header-wrap {
				margin-bottom: 24px;
				.month-year-wrapper {
					display: flex;
					width: 100%;
					align-items: center;
					justify-content: space-between;
					padding: 0 7px;
					.custom-month-year {
						font-weight: 400;
					}
					.dp__month_year_select {
						display: block;
						width: 17px;
						height: 17px;
						background: url('@/assets/images/pc/icon/ico_arrow_r5.svg') no-repeat center center;
						background-size: 8px auto;
						border: none;
						.hidden {
							font-size: 0;
							line-height: 0;
							opacity: 0;
							color: transparent;
						}
						&:first-child {
							transform: rotate(180deg);
						}
					}
				}
			}
			.dp__calendar {
				.dp__calendar_header_separator {
					display: none;
				}
				.dp__calendar_header {
					justify-content: center;
					align-items: center;
					gap: 10px;
					.dp__calendar_header_item {
						padding: 0;
						width: 32px;
						height: 32px;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 13px;
						color: #666666;
						font-weight: 400;
					}
				}
				.dp__calendar_row {
					margin: 10px 0 0;
					gap: 10px;
				}
			}
		}
	}
	:deep(.dp__menu_inner) {
		padding: 32px 32px 24px;
	}
	:deep(.dp__theme_light) {
    --dp-menu-border-color: #1e1e1e;
		--dp-hover-color: #dddddd;
  }
	:deep(.dp__cell_inner) {
    flex-direction: column;
		--dp-cell-size: 32px;
		border-radius: 6px;
		font-size: 11px;
		line-height: 16px;
		color: #1e1e1e;

    &.dp__active_date,
    &.dp__range_end,
    &.dp__range_start {
      background: #0a583f;
      color: #fff;
      font-weight: 700;
    }

		&.dp__active_date{
			background: #0a583f;
			color: #fff;
			font-weight: 700;
		}
		&.dp__cell_disabled {
			color: #cccccc;
		}
	}
	:deep(.dp__today) {
		position: relative;
		flex-direction: column;
		border: none;
    background: #0a583f;
		color: #fff !important;
	}

  &.positon-right {
    :deep(.dp--menu-wrapper) {
      left: auto !important;
      right: 0;
    }
  }
}
</style>
